// Styles
import "./dialog.scss";
// Components
import { DialogInput } from "./components/dialog-input/DialogInput";
import { MessageItem } from "./components/message-item/MessageItem";
import { EmptyList } from "../../../../../../../../shared/components/empty-list/EmptyList";
import { CircleLoader } from "../../../../../../../../shared/components/circle-loader/CircleLoader";
import { ProgressBar } from "./components/progress-bar/ProgressBar";
import { CompactSelect } from "../../../../../../../../shared/components/selects/compact-select/CompactSelect";
// Tools
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import gsap from "gsap";
// Utils
import { hideModuleThunk } from "../../../../utils/module-utils/hide-module";
import { formatISODate } from "../../../../../../../../shared/utils/formateDate";
// Types
import { RootState } from "../../../../../../../../shared/store/store";
import { store } from "../../../../../../../../shared/store/store";
import { ISessionData } from "../../../../../../../../shared/types/session.type";
import type { AdAccount } from "../../../../../../../../shared/types/dashboard-data.type";
import type { ICampaignWizardForm } from "../../../../../../../../shared/types/form.type";
// Actions
import {
	setFreezeUI,
	setSessionProgress,
	setSessionId,
	setActiveDraftId,
	setActiveChatKind,
	clearMessages,
	setActiveAdAccountId,
	setIsLoading,
	setSessionData,
} from "../../../../../../../../shared/store/slices/session.slice";
// Services
import { chatService } from "../../../../../../../../shared/websocket/services/chat.service";
import authService from "../../../../../../../../shared/websocket/services/auth.service";
import { sessionService } from "../../../../../../../../shared/websocket/services/session.service";
import wsClient from "../../../../../../../../shared/websocket/core/wsClient";
import { IChatMessagePayload } from "../../../../../../../../shared/types/chat.type";
import { useDashboardData } from "../../../../../../service/dashboard-data.service";
import { useCreateSession } from "./service/create-session.service";
import { useChatSessions } from "../history/service/history.service";
import { getInputDraft, setInputDraft } from "./utils/setInputDraft";
import {
	createDraftId,
	getDraftMessageText,
	removeDraft,
	upsertDraft,
} from "./utils/draft-sessions-storage";
import { bootstrapFormStateForSession } from "../form/utils/form-state-storage";
import { buildNestedCampaignContext } from "./utils/buildNestedCampaignContext";
import { SESSION_TYPE_BUMBLEBEE_STATUS } from "../../../../../../../../shared/types/bumblebee.type";
import {
	SELECTED_CUSTOMER_ID_CHANGED_EVENT,
	getSelectedCustomerAccountId,
	setSelectedCustomerAccountId,
} from "./utils/selectedCustomerIdStorage";

type Message = { role: string; content: string };
type SessionData = ISessionData & { isLoading?: boolean; messages?: Message[] };

export const Dialog = () => {
	// ----------- Dispatch
	const dispatch = useDispatch<any>();
	// ----------- Refs
	// ref на контейнер с сообщениями для автоскролла
	const messagesRef = useRef<HTMLDivElement | null>(null);
	const progressBarRef = useRef<HTMLDivElement>(null);
	// ----------- Variables
	const addChat = "/img/icons/large-64/add-chat.png";
	// ----------- States
	const [selectedAgentVersion, setSelectedAgentVersion] = useState<
		"pro" | "basic"
	>("pro");
	const userId: number | null = useSelector(
		(state: RootState) => state.user?.buyer_id ?? null,
	);
	const [selectedAccountKey, setSelectedAccountKey] = useState(
		() => getSelectedCustomerAccountId() ?? "",
	);
	const sessionId: string | null = useSelector(
		(state: RootState) => state.session.sessionId,
	);
	const activeDraftId: string | null = useSelector(
		(state: RootState) => state.session.activeDraftId,
	);
	const [inputValue, setInputValue] = useState<string>("");
	const sessionData: ISessionData | null = useSelector(
		(state: RootState) => state.session.sessionData,
	);
	const isLoading: boolean = useSelector(
		(state: RootState) => state.session.isLoading,
	);
	const sessionProgress: number = useSelector(
		(state: RootState) => state.session.sessionProgress,
	);
	const isFreezeUI: boolean = useSelector(
		(state: RootState) => state.session.freezeUI,
	);
	const activeChatKind = useSelector(
		(state: RootState) => state.session.activeChatKind,
	);
	const isBumblebeeChat = activeChatKind === SESSION_TYPE_BUMBLEBEE_STATUS;
	const { getValues } = useFormContext<ICampaignWizardForm>();

	// ----------- Data
	const { mutateAsync: createSession, isPending } = useCreateSession();
	const { data: dashboardData, isAwaitingDashboardContent } =
		useDashboardData();
	const accounts: AdAccount[] = useMemo(
		() =>
			(dashboardData?.ad_accounts?.data ?? []).filter(
				(acc) => acc.status === "ENABLED",
			),
		[dashboardData?.ad_accounts?.data],
	);
	const accountSelectItems = useMemo(
		() =>
			accounts.map((acc) => ({
				key: acc.account_id,
				node: <span>{`${acc.account_name} (${acc.account_id})`}</span>,
			})),
		[accounts],
	);
	const { data: sessionsData, isLoading: isSessionsLoading } = useChatSessions(
		userId != null ? String(userId) : undefined,
	);

	// Статус лоадера в зависимости от наличия сессий или загрузки текущего чата
	const shouldHideSessionLoader = useMemo(() => {
		if (userId == null) return false;
		if (isSessionsLoading) return false;
		const sessions = sessionsData?.data?.sessions;
		return Array.isArray(sessions) && sessions.length === 0;
	}, [userId, isSessionsLoading, sessionsData]);

	// Елементи списка версий агента
	const agentVersionSelectItems = useMemo(
		() => [
			{
				key: "pro",
				node: (
					<div className='dialog__agent-version-select_item'>
						<img src='/img/icons/rocket.png' alt='pro' />
						<div className='dialog__agent-version-select_text'>
							PRO - Створення багатьох РК з підтримкою складних конфігурацій
						</div>
					</div>
				),
			},
			{
				key: "basic",
				node: (
					<div className='dialog__agent-version-select_item'>
						<img src='/img/icons/lightning.png' alt='basic' />
						<div className='dialog__agent-version-select_text'>
							BASIC - Створення РК з базовими конфігураціями
						</div>
					</div>
				),
			},
		],
		[],
	);

	// Синхронизация селекта аккаунта с localStorage (обновляется Form при hydrate)
	useEffect(() => {
		const syncFromStorage = () => {
			setSelectedAccountKey(getSelectedCustomerAccountId() ?? "");
		};
		syncFromStorage();
		window.addEventListener(
			SELECTED_CUSTOMER_ID_CHANGED_EVENT,
			syncFromStorage,
		);
		return () =>
			window.removeEventListener(
				SELECTED_CUSTOMER_ID_CHANGED_EVENT,
				syncFromStorage,
			);
	}, [sessionId]);

	const resolvedAccountSelectValue = useMemo(() => {
		if (
			selectedAccountKey &&
			accountSelectItems.some((item) => item.key === selectedAccountKey)
		) {
			return selectedAccountKey;
		}
		return accountSelectItems[0]?.key;
	}, [accountSelectItems, selectedAccountKey]);

	useEffect(() => {
		if (!resolvedAccountSelectValue) return;
		dispatch(setActiveAdAccountId(resolvedAccountSelectValue));
	}, [dispatch, resolvedAccountSelectValue]);
	const handleInputChange = useCallback(
		(value: string) => {
			setInputValue(value);
			if (sessionId !== null) {
				setInputDraft(sessionId, value);
				return;
			}
			let draftId = activeDraftId;
			if (!draftId && value.trim().length > 0) {
				draftId = createDraftId();
				dispatch(setActiveDraftId(draftId));
			}
			if (draftId) {
				upsertDraft(draftId, value);
			}
		},
		[sessionId, activeDraftId, dispatch],
	);

	useEffect(() => {
		if (sessionId !== null) {
			setInputValue(getInputDraft(sessionId));
			return;
		}
		setInputValue(getDraftMessageText(activeDraftId));
	}, [sessionId, activeDraftId]);

	// ----------- Functions
	const handleSendMessage = async (messageText: string) => {
		// Отправка сообщения на сервер
		if (messageText.trim().length === 0) return;
		if (isBumblebeeChat) return;
		if (!wsClient.isOpen()) {
			console.warn("WebSocket is not connected");
			return;
		}

		const draftIdAtSend = activeDraftId;
		let activeSessionId = sessionId;
		let formSnapshotForPayload: ICampaignWizardForm | null = null;

		// Находим выбранный аккаунт
		const selectedAccount = accounts.find(
			(acc) => acc.account_id === resolvedAccountSelectValue,
		);
		const customerIdForSend =
			selectedAccount?.account_id.replace(/-/g, "") ?? "";

		if (!customerIdForSend) {
			console.warn("No Google Ads account selected for chat message");
			return;
		}

		// Если сессия не выбрана, создаем новую сессию (новая сессия = новый чат)
		if (activeSessionId === null) {
			const result = await createSession({
				user_id: userId?.toString() ?? "",
				title: "Нова сесія",
			});
			const newId = result?.data?.session_id ?? null;
			if (!newId) return;

			const formSnapshot = getValues();
			const formDraft: ICampaignWizardForm = {
				...formSnapshot,
				user_id: userId?.toString() ?? "",
				campaign_context: {
					...formSnapshot.campaign_context,
					customer_id:
						selectedAccount?.account_id.replace(/-/g, "") ??
						formSnapshot.campaign_context.customer_id,
					account_name:
						selectedAccount?.account_name ??
						formSnapshot.campaign_context.account_name,
				},
			};
			bootstrapFormStateForSession(newId, formDraft);
			formSnapshotForPayload = formDraft;

			const switchReady = sessionService.waitForSessionSwitch(newId);

			dispatch(setActiveChatKind("assistant"));
			dispatch(
				setSessionData({
					ok: true,
					type: "session.switch_success",
					session_id: newId,
					previous_session_id: "",
					count: 0,
					messages: [],
				}),
			);
			dispatch(setSessionId(newId));
			activeSessionId = newId;

			authService.requestAuth({
				user_id: userId?.toString() ?? "",
				session_id: newId,
				campaign_context: {
					customer_id: customerIdForSend,
					account_name: selectedAccount?.account_name ?? "",
				},
			});

			try {
				await switchReady;
			} catch (error) {
				console.warn("Session switch did not complete before send", error);
			}
		}

		// Юзер успел уйти в другой чат, пока создавалась/переключалась сессия —
		// отправка отменяется (сообщение не уйдет и не аппендится в чужой чат)
		if (store.getState().session.sessionId !== activeSessionId) {
			return;
		}

		// Показываем прогресс бар
		gsap.to(progressBarRef.current, {
			opacity: 1,
			duration: 0.3,
			ease: "power2.inOut",
		});
		// Создаем payload для отправки на сервер
		const formSnapshot = formSnapshotForPayload ?? getValues();
		const campaignContext = buildNestedCampaignContext(formSnapshot, {
			customer_id: customerIdForSend,
			account_name: selectedAccount?.account_name,
		});
		const payload: IChatMessagePayload = {
			type: "chat.message",
			content: messageText,
			agent_version: selectedAgentVersion,
			campaign_context: campaignContext,
			// Явный роутинг по сессии: сервер использует payload session_id
			session_id: activeSessionId ?? undefined,
		};

		// Добавляем сообщение в список сообщений
		chatService.assistantMessage({
			role: "user",
			content: messageText,
			ts: new Date().toISOString(),
			type: "message",
			meta: {
				event: "chat.message",
				customer_id: campaignContext.customer_id ?? customerIdForSend,
			},
		});
		// Замораживаем UI
		dispatch(setFreezeUI(true));
		// Отправляем сообщение на сервер
		chatService.sendMessage(payload);
		setInputValue("");
		if (draftIdAtSend) {
			removeDraft(draftIdAtSend);
			dispatch(setActiveDraftId(null));
		}
		if (activeSessionId) {
			setInputDraft(activeSessionId, "");
		}
	};

	// ----------- Effects
	useEffect(() => {
		if (!isFreezeUI) {
			dispatch(setSessionProgress(0));
		}
	}, [isFreezeUI, dispatch]);

	useEffect(() => {
		const el = messagesRef.current;
		if (!el) return;
		// Скроллим в низ после рендера
		requestAnimationFrame(() => {
			el.scrollTop = el.scrollHeight;
		});
	}, [sessionId, sessionData?.messages?.length]);

	useEffect(() => {
		if (sessionProgress >= 100) {
			setTimeout(() => {
				// Скрываем прогресс бар
				gsap.to(progressBarRef.current, {
					opacity: 0,
					duration: 0.3,
					ease: "power2.inOut",
					onComplete: () => {
						// Сбрасываем прогресс
						dispatch(setSessionProgress(0));
					},
				});
			}, 1000);
		}
	}, [sessionProgress]);

	useEffect(() => {
		if (!shouldHideSessionLoader || sessionId !== null) return;
		dispatch(setIsLoading(false));
	}, [dispatch, sessionId, shouldHideSessionLoader]);

	return (
		<div className='dialog window'>
			<div className='window__header'>
				<div className='window__header_left'>
					<img src='/img/icons/chat.png' alt='chat icon' />
					<div>Чат</div>
				</div>
				<div className='window__header_center dialog__header-center'>
					{!isBumblebeeChat && accountSelectItems.length > 0 && (
						<CompactSelect
							className='dialog__account-select'
							items={accountSelectItems}
							value={resolvedAccountSelectValue}
							onSelect={(key) => {
								setSelectedAccountKey(key);
								setSelectedCustomerAccountId(key);
								dispatch(setActiveAdAccountId(key));
							}}
						/>
					)}
					{!isBumblebeeChat && (
						<CompactSelect
							className='dialog__agent-version-select'
							items={agentVersionSelectItems}
							onSelect={(key) => setSelectedAgentVersion(key as "pro" | "basic")}
						/>
					)}
				</div>
				<div className='window__header_right'>
					<div
						className='window__header_right_close-button'
						role='button'
						aria-label='hide chat window'
						onClick={() => dispatch(hideModuleThunk("chat_window"))}>
						<img src='/img/icons/close.png' alt='close button' />
					</div>
				</div>
			</div>
			<div className='window__body no-vertical-paddings dialog__space'>
				<ProgressBar ref={progressBarRef} progress={sessionProgress} />
				{!shouldHideSessionLoader &&
					(isLoading ||
						(isAwaitingDashboardContent &&
							sessionsData?.data?.count &&
							sessionsData?.data?.count > 0)) && (
						<div className='window__loader'>
							<CircleLoader />
						</div>
					)}
				{sessionId === null &&
					!isAwaitingDashboardContent &&
					(!isLoading || shouldHideSessionLoader) && (
						<div className='window__loader'>
							<EmptyList
								image={addChat}
								text={
									"Напишіть перше повідомлення, щоб почати діалог з ассистентом"
								}
							/>
						</div>
					)}

				<div className='window__body_list without-padding-top'>
					{sessionId !== null && sessionData !== null && (
						<div
							className='window__body_list-inner dialog__messages-space'
							ref={messagesRef}>
							{sessionData.messages.map((item, idx) => {
								return (
									<MessageItem
										key={idx}
										author={item.role}
										message={item.content}
										date={formatISODate(item.ts)}
										messageType={item.type}
										campaignId={item.meta?.campaign_id}
										campaignName={item.meta?.campaign_name}
									/>
								);
							})}
						</div>
					)}
				</div>
				<div className='dialog__bottom'>
					{isBumblebeeChat ? (
						<div className='dialog__bottom_message dialog__bottom_message--readonly'>
							Це інформаційний чат BumbleBee. Надсилання повідомлень наразі
							недоступне.
						</div>
					) : (
						<>
							<DialogInput
								inputValue={inputValue}
								setInputValue={handleInputChange}
								handleSendMessage={handleSendMessage}
							/>
							<div className='dialog__bottom_message'>
								Агент може робити помилки, перевіряйте отриману інформацію
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
